// Which media an Event hero should show — the decision, as a pure function.
//
// ── ONE DETERMINISTIC ORDER, WRITTEN DOWN ────────────────────────────────────────────
//
//   1. a CMS video MediaAsset, if the template names one and it resolves
//   2. the event's legacy featuredVideoUrl
//   3. a CMS image MediaAsset, if the template names one and it resolves
//   4. the event's legacy featuredImageUrl
//   5. nothing — the hero renders its own empty state
//
// A video's poster follows its own short chain: an explicit poster MediaAsset, then the
// CMS image, then the event's legacy image, then no poster at all.
//
// ── AN UNRESOLVABLE ASSET IS A MISS, NOT A FAILURE ───────────────────────────────────
//
// Object storage is not configured in production, so every MediaAsset lookup currently
// returns null. That is the normal case, not an error case: each step falls to the next,
// and today every event lands on its legacy URL exactly as it did before the CMS existed.
// A template naming an asset that cannot be resolved shows the event's own artwork rather
// than an empty hero or a broken image.
//
// ── DELIBERATELY IMPORT-FREE ─────────────────────────────────────────────────────────
//
// No React, no hooks, no fetch, so node can execute it directly and every rung of the
// chain is asserted without a browser. scripts/verify-hero-media.mjs does that. The hook
// that feeds it lives in heroMedia.ts.

export interface HeroMediaProps {
  videoMediaAssetId?: string | null;
  posterMediaAssetId?: string | null;
  mediaAssetId?: string | null;
}

export interface ResolvedHeroMedia {
  imageUrl: string | null;
  videoUrl: string | null;
  videoWebmUrl: string | null;
  /** Which rung of the chain answered. Used by the contract test and by diagnostics. */
  source: 'cms_video' | 'legacy_video' | 'cms_image' | 'legacy_image' | 'none';
}

/** The already-resolved inputs the decision is made from. */
export interface HeroMediaInputs {
  cmsVideo: string | null;
  cmsPoster: string | null;
  cmsImage: string | null;
  legacyVideo: string | null;
  legacyWebm: string | null;
  legacyImage: string | null;
}

/**
 * The decision itself, as a pure function.
 *
 * Separated from the hook so it can be exercised directly — every rung of the chain, and
 * every combination of what is and is not available — without a browser or a React
 * renderer. scripts/verify-hero-media.mjs does exactly that.
 */
export function chooseHeroMedia({
  cmsVideo, cmsPoster, cmsImage, legacyVideo, legacyWebm, legacyImage,
}: HeroMediaInputs): ResolvedHeroMedia {
  // The poster chain, resolved first because both video branches need it.
  const poster = cmsPoster ?? cmsImage ?? legacyImage ?? null;

  if (cmsVideo) {
    // A CMS video supersedes the legacy one entirely, including its WebM companion: the
    // two are different files and pairing an override with the old alternate source
    // would let a browser pick the one nobody chose.
    return { imageUrl: poster, videoUrl: cmsVideo, videoWebmUrl: null, source: 'cms_video' };
  }

  if (legacyVideo || legacyWebm) {
    return {
      imageUrl: poster,
      videoUrl: legacyVideo,
      videoWebmUrl: legacyWebm,
      source: 'legacy_video',
    };
  }

  if (cmsImage) {
    return { imageUrl: cmsImage, videoUrl: null, videoWebmUrl: null, source: 'cms_image' };
  }

  if (legacyImage) {
    return { imageUrl: legacyImage, videoUrl: null, videoWebmUrl: null, source: 'legacy_image' };
  }

  return { imageUrl: null, videoUrl: null, videoWebmUrl: null, source: 'none' };
}
